import type { ReactNode } from 'react'

import type { QrCameraError } from './model/camera-error'
import type { ScanGateMode } from './model/scan-gate'
import {
  QrScannerStatus,
  QrScanHandlerResult,
  useQrScanner
} from './useQrScanner'

export type { QrCameraError, QrCameraErrorKind } from './model/camera-error'
export type { ScanGateMode } from './model/scan-gate'
export type { QrScannerStatus, QrScanHandlerResult } from './useQrScanner'
export { useQrScanner } from './useQrScanner'

/** Estado entregue aos slots do consumidor. */
export type QrCodeReaderState = {
  status: QrScannerStatus
  error: QrCameraError | null
  retry: () => void
}

type Slot = ReactNode | ((state: QrCodeReaderState) => ReactNode)

export type QrCodeReaderProps = {
  onSuccess: (
    code: string
  ) => QrScanHandlerResult | Promise<QrScanHandlerResult>
  /** Suspende a leitura sem derrubar a câmera; alternar o valor rearma o leitor. */
  disabled?: boolean
  /** Aplicado na moldura da câmera, como no leitor anterior. */
  className?: string
  /** `single` (padrão): uma leitura por acionamento. `continuous`: várias, com dedupe. */
  mode?: ScanGateMode
  /** Intervalo mínimo entre leituras aceitas. Só faz sentido em `continuous`. */
  cooldownMs?: number
  preferredCamera?: 'environment' | 'user'
  scanRegionRatio?: number
  onError?: (error: QrCameraError | null) => void
  /**
   * Escape opcional para quando a câmera não colabora — na prática o campo
   * "ou digite o código". O markup é do consumidor: a bottom nav não usa;
   * desafio, quiz e modal usam cada um o seu.
   */
  fallback?: Slot
  /** Conteúdo sobreposto à câmera (ex.: "validando..."). */
  overlay?: Slot
  /** Mensagem de erro de câmera embutida. Desligue para renderizar a sua. */
  showCameraError?: boolean
}

const renderSlot = (
  slot: Slot | undefined,
  state: QrCodeReaderState
): ReactNode => (typeof slot === 'function' ? slot(state) : (slot ?? null))

export const QrCodeReader = ({
  onSuccess,
  disabled = false,
  className,
  mode = 'single',
  cooldownMs = 0,
  preferredCamera = 'environment',
  scanRegionRatio,
  onError,
  fallback,
  overlay,
  showCameraError = true
}: QrCodeReaderProps) => {
  const { videoRef, status, error, retry } = useQrScanner({
    onSuccess,
    disabled,
    mode,
    cooldownMs,
    preferredCamera,
    scanRegionRatio,
    onError
  })

  const state: QrCodeReaderState = { status, error, retry }

  return (
    <>
      {/* `relative` é requisito do overlay de região de leitura, que o
          qr-scanner posiciona em absoluto ao lado do vídeo. */}
      <div className={className ? `relative ${className}` : 'qr-reader'}>
        <video
          ref={videoRef}
          muted
          playsInline
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            aspectRatio: 1 / 1
          }}
        />
        {renderSlot(overlay, state)}
      </div>

      {showCameraError && !!error && (
        <p
          role="alert"
          className="mt-2 text-center text-base font-semibold text-danger"
        >
          {error.message}
        </p>
      )}

      {renderSlot(fallback, state)}
    </>
  )
}
